/**
 * WebSocket Connection Manager
 * 
 * Provides a centralized manager for WebSocket connections with support for:
 * - Automatic reconnection
 * - Event subscription
 * - Handling connection lifecycle
 */

type EventCallback = (data: any) => void;
type DisconnectReason = 'normal' | 'error' | 'timeout' | 'forced';

export enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
}

interface ConnectionOptions {
  /** Automatically attempt to reconnect on disconnection */
  autoReconnect?: boolean;
  /** Maximum number of reconnection attempts */
  maxReconnectAttempts?: number;
  /** Base delay for reconnection (increases with backoff) */
  reconnectBaseDelay?: number;
  /** Maximum delay for reconnection attempts */
  maxReconnectDelay?: number;
  /** Debug mode - log all events to console */
  debug?: boolean;
}

const DEFAULT_OPTIONS: ConnectionOptions = {
  autoReconnect: true,
  maxReconnectAttempts: 10,
  reconnectBaseDelay: 1000,
  maxReconnectDelay: 30000,
  debug: false,
};

/**
 * WebSocket manager for handling real-time connections
 */
export class WebSocketManager {
  private socket: WebSocket | null = null;
  private url: string = '';
  private options: ConnectionOptions;
  private eventListeners: Map<string, Set<EventCallback>> = new Map();
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private reconnectAttempts: number = 0;
  private reconnectTimeout: number | null = null;
  private connectionStateListeners: Set<(state: ConnectionState) => void> = new Set();
  private heartbeatInterval: number | null = null;

  /**
   * Create a new WebSocketManager
   * @param options Connection options
   */
  constructor(options: ConnectionOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Connect to a WebSocket endpoint
   * @param url The WebSocket URL to connect to
   */
  connect(url: string): void {
    // Don't reconnect if already connected to the same URL
    if (this.socket && this.url === url && this.socket.readyState === WebSocket.OPEN) {
      return;
    }

    // Clean up any existing connection
    this.cleanup();

    this.url = url;
    this.setConnectionState(ConnectionState.CONNECTING);
    this.log(`Connecting to ${url}`);

    try {
      this.socket = new WebSocket(url);
      this.setupEventHandlers();
    } catch (error) {
      this.log(`Error creating WebSocket: ${error}`);
      this.handleDisconnect('error');
    }
  }

  /**
   * Manually disconnect the WebSocket
   */
  disconnect(): void {
    if (!this.socket) return;

    this.log('Manual disconnect requested');
    this.options.autoReconnect = false; // Prevent auto-reconnect on manual disconnect
    
    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.close(1000, 'Manual disconnect');
    }
    this.cleanup();
    this.setConnectionState(ConnectionState.DISCONNECTED);
  }

  /**
   * Send a message through the WebSocket
   * @param data The data to send
   * @returns true if sent successfully, false otherwise
   */
  send(data: any): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.log('Cannot send message: WebSocket not connected');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
      return true;
    } catch (error) {
      this.log(`Error sending message: ${error}`);
      return false;
    }
  }

  /**
   * Subscribe to a specific event type
   * @param eventType The event type to subscribe to
   * @param callback Callback function when event is received
   * @returns Unsubscribe function
   */
  subscribe(eventType: string, callback: EventCallback): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }

    const listeners = this.eventListeners.get(eventType)!;
    listeners.add(callback);

    return () => {
      const listeners = this.eventListeners.get(eventType);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Subscribe to connection state changes
   * @param callback Callback function when connection state changes
   * @returns Unsubscribe function
   */
  onConnectionStateChange(callback: (state: ConnectionState) => void): () => void {
    this.connectionStateListeners.add(callback);
    
    // Immediately notify with current state
    callback(this.connectionState);
    
    return () => {
      this.connectionStateListeners.delete(callback);
    };
  }

  /**
   * Get the current connection state
   * @returns Current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Send a ping to keep the connection alive
   * Useful for preventing timeouts
   */
  ping(): void {
    this.send({ type: 'ping', timestamp: Date.now() });
  }

  /**
   * Set up a heartbeat interval to keep connection alive
   * @param intervalMs Interval in milliseconds
   */
  setupHeartbeat(intervalMs: number = 30000): void {
    this.clearHeartbeat();
    this.heartbeatInterval = window.setInterval(() => {
      this.ping();
    }, intervalMs);
  }

  /**
   * Clear the heartbeat interval
   */
  clearHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // Private methods

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = this.handleOpen.bind(this);
    this.socket.onmessage = this.handleMessage.bind(this);
    this.socket.onclose = this.handleClose.bind(this);
    this.socket.onerror = this.handleError.bind(this);
  }

  private handleOpen(_event: Event): void {
    this.log('WebSocket connected');
    this.reconnectAttempts = 0;
    this.setConnectionState(ConnectionState.CONNECTED);
    this.setupHeartbeat();
    
    // Notify subscribers
    this.notifySubscribers('connection', { state: 'connected' });
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      const eventType = data.type || 'message';
      
      this.log(`Received ${eventType} event`);
      
      // Handle pong response
      if (eventType === 'pong') {
        return;
      }
      
      // Notify subscribers
      this.notifySubscribers(eventType, data.data || data);
      
      // Also notify generic 'message' subscribers
      if (eventType !== 'message') {
        this.notifySubscribers('message', data);
      }
    } catch (error) {
      this.log(`Error parsing message: ${error}`);
      // For non-JSON messages, send as raw
      this.notifySubscribers('raw', { data: event.data });
    }
  }

  private handleClose(event: CloseEvent): void {
    const reason = event.wasClean ? 'normal' : 'error';
    this.log(`WebSocket closed: ${event.code} - ${event.reason || 'No reason provided'}`);
    this.handleDisconnect(reason);
  }

  private handleError(_event: Event): void {
    this.log('WebSocket error');
    this.handleDisconnect('error');
  }

  private handleDisconnect(reason: DisconnectReason): void {
    this.cleanup();
    this.setConnectionState(ConnectionState.DISCONNECTED);
    
    // Notify subscribers
    this.notifySubscribers('disconnection', { reason });
    
    // Attempt to reconnect if enabled
    if (this.options.autoReconnect) {
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    const maxAttempts = this.options.maxReconnectAttempts || 10;
    
    if (this.reconnectAttempts >= maxAttempts) {
      this.log(`Maximum reconnection attempts (${maxAttempts}) reached`);
      return;
    }
    
    this.reconnectAttempts++;
    
    // Exponential backoff with jitter
    const baseDelay = this.options.reconnectBaseDelay || 1000;
    const maxDelay = this.options.maxReconnectDelay || 30000;
    let delay = Math.min(baseDelay * Math.pow(1.5, this.reconnectAttempts - 1), maxDelay);
    
    // Add jitter to prevent reconnection storms
    delay = delay * (0.8 + Math.random() * 0.4);
    
    this.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    this.setConnectionState(ConnectionState.RECONNECTING);
    this.reconnectTimeout = window.setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect(this.url);
    }, delay);
  }

  private notifySubscribers(eventType: string, data: any): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          this.log(`Error in subscriber callback: ${error}`);
        }
      });
    }
  }

  private cleanup(): void {
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      
      if (this.socket.readyState === WebSocket.OPEN || 
          this.socket.readyState === WebSocket.CONNECTING) {
        this.socket.close();
      }
      this.socket = null;
    }
    
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    this.clearHeartbeat();
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.connectionStateListeners.forEach(listener => {
      try {
        listener(state);
      } catch (error) {
        this.log(`Error in connection state listener: ${error}`);
      }
    });
  }

  private log(message: string): void {
    if (this.options.debug) {
      console.log(`[WebSocketManager] ${message}`);
    }
  }
}

// Create singleton instance
const websocketManager = new WebSocketManager({ debug: true });
export default websocketManager;
